/**
 * Filter state ↔ URL codec (C3 `FilterBar`).
 *
 * A filtered view has to be shareable — "look at this" is how a teammate hands
 * work over (UX v0.3 §4). That means filter state lives in the URL, not in
 * component state, and the encoding is a pure function so a link built by one
 * screen is read identically by another.
 */

export type FilterValue = string | string[] | null;
export type FilterValues = Record<string, FilterValue>;

const PREFIX = "f_";

/**
 * Read filter values out of a router search object.
 *
 * Only `f_`-prefixed keys are claimed, so a module's own search params (a
 * cursor, a selected row) survive untouched.
 */
export function parseFilters(search: Record<string, unknown>): FilterValues {
  const out: FilterValues = {};
  for (const [key, raw] of Object.entries(search)) {
    if (!key.startsWith(PREFIX)) continue;
    const name = key.slice(PREFIX.length);
    if (Array.isArray(raw)) out[name] = raw.map(String);
    else if (raw == null || raw === "") out[name] = null;
    else out[name] = String(raw);
  }
  return out;
}

/**
 * Merge new filter values into an existing search object.
 *
 * A cleared filter is REMOVED from the URL rather than written as empty: an
 * `f_status=` in a shared link reads as "filtered to nothing", which is a
 * different and false statement.
 */
export function applyFilters(
  search: Record<string, unknown>,
  values: FilterValues,
): Record<string, unknown> {
  const next = { ...search };
  for (const [name, value] of Object.entries(values)) {
    const key = `${PREFIX}${name}`;
    if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}

/**
 * How many filters are actually narrowing the view — for the "Clear"
 * affordance.
 *
 * `defaults` names each filter's "this is not really a filter" value — e.g.
 * the week picker's own default week. A value equal to its default is not
 * narrowing anything, so it does not count and does not earn a "Clear 1
 * filter" button; a control that always carries a value (a period picker)
 * would otherwise read as permanently filtered.
 */
export function activeFilterCount(values: FilterValues, defaults?: FilterValues): number {
  return Object.entries(values).filter(([name, v]) => {
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return false;
    if (defaults && name in defaults && defaults[name] === v) return false;
    return true;
  }).length;
}

/**
 * Only the `f_` filter params of a search object - what carries across a tab
 * switch inside one module (KET-6592). The filters describe the data the whole
 * module is showing, so "UK, 90 days, weekly" must survive a click from
 * Overview to UK and back; a tab's own non-filter params (a cursor, a selected
 * row) belong to that tab and are left behind.
 */
export function carryFilters(search: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (key.startsWith(PREFIX)) out[key] = value;
  }
  return out;
}
